import React from 'react';
import { Icon, Accordion, TextField, SelectMenu, Checkbox } from '@scale/scaleui';

import clsx from 'clsx';
import {
  SearchResultAction as ISearchResultAction,
  SearchResultActionType,
} from '@scale/shared/types/chat';
import {
  validateAction,
  SearchResultActionsRequireInputOutput,
  DEFAULT_SEARCH_ACTION,
} from '../utils';

const SearchResultActionTypeOptions = [
  ...Object.values(SearchResultActionType).map(v => ({
    id: v,
    name: v,
    isHeader: v === SearchResultActionType.Placeholder,
  })),
];

interface Props {
  action: ISearchResultAction;
  actionIndex: number;
  onRemove: () => void;
  onEdit: (newAction: ISearchResultAction) => void;
  validationResult: ReturnType<typeof validateAction>;
}

const SearchResultAction: React.FC<Props> = ({
  action,
  actionIndex,
  onRemove,
  onEdit,
  validationResult,
}) => {
  return (
    <Accordion
      startOpen
      header={
        <div
          className={clsx('flex justify-between w-full mr-4 items-center', {
            'text-danger-600': !validationResult.isValid,
          })}
        >
          <span className="max-w-[500px] overflow-hidden whitespace-nowrap text-left">
            Action #{actionIndex + 1}{' '}
            {validationResult.isValid ? `(${action.actionType} - ${action.currentUrl})` : ''}
          </span>
          <div onClick={onRemove}>
            <Icon variant="danger" size="md" icon="trash-alt" tooltip="Remove" />
          </div>
        </div>
      }
      target={
        <div className="flex flex-col gap-1">
          <SelectMenu
            label="Action Type"
            options={SearchResultActionTypeOptions}
            selectedOptionId={action.actionType}
            setSelected={id => {
              onEdit({ ...DEFAULT_SEARCH_ACTION, actionType: id });
            }}
            helperText={
              <p className="text-danger-600">{validationResult.actionTypeValidationResult}</p>
            }
          />
          <TextField
            label="Thoughts"
            labelDescription="Thought process. Explain why you are making this action."
            id="thoughts"
            value={action.thoughts}
            onChange={e => {
              onEdit({ ...action, thoughts: e.target.value });
            }}
            helperText={
              <p className="text-danger-600">{validationResult.thoughtsValidationResult}</p>
            }
          />
          {SearchResultActionsRequireInputOutput.includes(action.actionType) && (
            <>
              <TextField
                label="Input"
                labelDescription={
                  action.actionType === SearchResultActionType.ControlF
                    ? 'Please input the Control-F keyword you used.'
                    : 'Please input the text read.'
                }
                id="input"
                value={action.input}
                onChange={e => {
                  onEdit({ ...action, input: e.target.value });
                }}
                helperText={
                  <p className="text-danger-600">{validationResult.inputValidationResult}</p>
                }
              />
              <TextField
                label="Output"
                labelDescription={
                  action.actionType === SearchResultActionType.ControlF
                    ? 'Please input the read text after your used of Control-F text search.'
                    : 'Please input your knowledge gain from reading the text.'
                }
                id="output"
                value={action.output}
                onChange={e => {
                  onEdit({ ...action, output: e.target.value });
                }}
                helperText={
                  <p className="text-danger-600">{validationResult.outputValidationResult}</p>
                }
              />
              <Checkbox
                label="The action is on the original snippet"
                checked={!!action.originalSnippet}
                onChange={e => {
                  onEdit({ ...action, originalSnippet: e.target.checked });
                }}
              />
            </>
          )}
          <TextField
            label="Current URL"
            id="url"
            value={action.currentUrl}
            onChange={e => {
              onEdit({ ...action, currentUrl: e.target.value });
            }}
            helperText={
              <p className="text-danger-600">{validationResult.currentURLValidationResult}</p>
            }
          />
        </div>
      }
    />
  );
};

export default SearchResultAction;
